import { Colors } from '@oy/uicore';
import {
  Button,
  Checkbox,
  PasswordField,
  Spacer,
  Text,
  TextField,
} from '@oy/uidesktop';
import { Controller } from 'react-hook-form';
import { Link } from 'react-router-dom';
import { useFormHelper, useTranslate } from 'src/hooks';
import styles from './LoginForm.module.css';
import { LoginFormData } from './formSchema';
import { useLoginFormImpl } from './useLoginFormImpl';

type LoginFormProps = {
  onSubmit: (data: LoginFormData) => void;
  initialValues?: Partial<LoginFormData>;
  isRelogin?: boolean;
  isLoginSuperAdmin?: boolean;
  isSubmitting?: boolean;
};

export const LoginForm = ({
  initialValues,
  isRelogin = false,
  isLoginSuperAdmin = false,
  isSubmitting,
  onSubmit,
}: LoginFormProps) => {
  const { control, watch, trigger, handleSubmit } = useLoginFormImpl({
    initialValues,
    isRelogin,
  });
  const { bindInput } = useFormHelper<LoginFormData>();

  const { getTranslation } = useTranslate();

  const watchIsLoginSubAccount = watch('isLoginSubAccount');

  const buildSubAccountField = () => {
    if (isRelogin || isLoginSuperAdmin) return null;
    return (
      <div className={styles.subAccountFieldContainer}>
        <Controller
          control={control}
          name="subAccountUsername"
          render={({ field, fieldState }: any) => (
            <TextField
              {...bindInput(field, fieldState)}
              value={
                field.value as 'string | number | readonly string[] | undefined'
              }
              label={
                <Text
                  textStyle={Text.Style.BodySmSemibold}
                  color={Colors.BLACK40}
                >
                  {getTranslation('common_sub_account').toUpperCase()}
                </Text>
              }
              placeholder={getTranslation(
                'common_form_inputSubAccountUsername',
              )}
              disabled={!watchIsLoginSubAccount}
              onChange={e => {
                e.target.value = e.target.value.toLowerCase();
                return field.onChange(e);
              }}
            />
          )}
        />
        <Spacer bottom={Spacer.Size.XS} />
        <Controller
          control={control}
          name="isLoginSubAccount"
          render={({ field }) => (
            <Checkbox
              id="checkSubAcc"
              title={getTranslation('login_form_asSubAccount_label')}
              checked={field.value}
              onChange={e => {
                field.onChange(e);
                trigger('subAccountUsername');
              }}
            />
          )}
        />
      </div>
    );
  };

  return (
    <form onSubmit={handleSubmit(onSubmit)}>
      <Controller
        control={control}
        name="username"
        render={({ field, fieldState }: any) => (
          <div className={styles.usernameField}>
            <TextField
              {...bindInput(field, fieldState)}
              value={
                field.value as 'string | number | readonly string[] | undefined'
              }
              name={field.name}
              label={
                <Text
                  textStyle={Text.Style.BodySmSemibold}
                  color={Colors.BLACK40}
                >
                  {getTranslation(
                    `login_form_username${
                      !isRelogin ? 'SuperAdmin' : ''
                    }_label`,
                  ).toUpperCase()}
                </Text>
              }
              placeholder={getTranslation('common_form_inputUsername')}
              readOnly={isRelogin}
              onChange={e => {
                e.target.value = e.target.value.toLowerCase();
                return field.onChange(e);
              }}
            />
          </div>
        )}
      />
      {buildSubAccountField()}
      <Controller
        control={control}
        name="password"
        render={({ field, fieldState }: any) => (
          <div className={styles.passwordFieldContainer}>
            <div className={styles.passwordFieldLabel}>
              <Text
                textStyle={Text.Style.BodySmSemibold}
                color={Colors.BLACK40}
              >
                {getTranslation('common_password').toUpperCase()}
              </Text>
              {!isRelogin && (
                <Link
                  className={styles.backToLogin}
                  to="/forgot-password"
                  id="lnkForgotPassword"
                >
                  <Text
                    textStyle={Text.Style.BodySmSemibold}
                    color={Colors.BLUE100}
                  >
                    {getTranslation('login_forgotPassword')}
                  </Text>
                </Link>
              )}
            </div>
            <PasswordField
              {...bindInput(field, fieldState)}
              value={
                field.value as 'string | number | readonly string[] | undefined'
              }
              name="password"
              placeholder={getTranslation('login_form_password_placeholder')}
            />
          </div>
        )}
      />
      <Button
        name="login"
        type="submit"
        fullWidth={true}
        disabled={isSubmitting}
      >
        {getTranslation('common_login')}
      </Button>
    </form>
  );
};
